import React from "react";
import { Box, Skeleton, styled } from "@mui/material";
import { useSkeletonAnimation } from "../../hooks/useSkeletonAnimation";

const Container = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  width: "100%",
  borderRadius: theme.spacing(1),
}));

interface GridProps {
  $twoColumn: boolean;
}
const Grid = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$twoColumn",
})<GridProps>(({ theme, $twoColumn }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr",
  width: "100%",
  ...($twoColumn && {
    [theme.breakpoints.up("sm")]: {
      gridTemplateColumns: "1fr 1fr",
    },
  }),
}));

type ProductDetailSkeletonProps = {
  variant: "desktop" | "mobile";
};

/**
 * The product page's loading state (EB-543).
 *
 * Mirrors the real order the page settles into after EB-542 — gallery, then the purchase block
 * above the description — so the shopper's eye is already where the buy buttons are about to
 * appear. It replaces a literal `"..."`, which read more like a broken page than a loading one.
 */
const ProductDetailSkeleton: React.FC<ProductDetailSkeletonProps> = ({ variant }) => {
  const animation = useSkeletonAnimation();
  const isMobile = variant === "mobile";

  const gallery = (
    <Box px={2} py={isMobile ? 0 : 2}>
      {/* The wrapper owns the square. `Skeleton` writes its own `height`, so `aspect-ratio` on the
          skeleton itself collapses to a thin bar — the box has to come from outside it. */}
      <Box sx={{ width: "100%", aspectRatio: "1 / 1" }}>
        <Skeleton animation={animation} variant="rounded" width="100%" height="100%" />
      </Box>
      <Box display="flex" justifyContent="center" gap={0.75} mt={1}>
        {[0, 1, 2, 3].map((dot) => (
          <Skeleton key={dot} animation={animation} variant="circular" width={8} height={8} />
        ))}
      </Box>
      {/* The thumbnail strip is desktop-only, exactly as in `ItemGallery`. */}
      {!isMobile && (
        <Box display="flex" gap={1} mt={1}>
          {[0, 1, 2, 3].map((thumb) => (
            <Skeleton key={thumb} animation={animation} variant="rounded" width={56} height={56} />
          ))}
        </Box>
      )}
    </Box>
  );

  const purchaseBlock = (
    <Box mt={isMobile ? 1 : 2} p={1.5} display="flex" flexDirection="column" gap={1}>
      <Skeleton animation={animation} variant="text" width={110} height={24} />
      <Skeleton animation={animation} variant="rounded" width="100%" height={40} />
      <Skeleton
        animation={animation}
        variant="rounded"
        width="100%"
        height={38}
        sx={{ borderRadius: 999 }}
      />
      <Skeleton
        animation={animation}
        variant="rounded"
        width="100%"
        height={38}
        sx={{ borderRadius: 999 }}
      />
    </Box>
  );

  // Name, rating, category, price.
  const header = (
    <Box px={1}>
      <Skeleton animation={animation} variant="text" width="70%" height={38} />
      <Skeleton animation={animation} variant="text" width={120} height={20} />
      <Skeleton animation={animation} variant="rounded" width={88} height={24} sx={{ mt: 0.5 }} />

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
        <Skeleton animation={animation} variant="text" width={80} height={28} />
        <Skeleton animation={animation} variant="text" width={130} height={28} />
      </Box>
    </Box>
  );

  const description = (
    <Box px={1}>
      <Skeleton animation={animation} variant="text" width={140} height={28} sx={{ mt: 2 }} />
      {["95%", "100%", "88%", "60%"].map((width) => (
        <Skeleton key={width} animation={animation} variant="text" width={width} height={16} />
      ))}
    </Box>
  );

  // Mobile puts the gallery between the price and the purchase block; desktop stacks the whole
  // right-hand column beside it. Both match the order the loaded page settles into.
  if (isMobile) {
    return (
      <Container data-testid="product-detail-skeleton" aria-busy>
        <Box p={2}>{header}</Box>
        {gallery}
        <Box p={1}>
          {purchaseBlock}
          {description}
        </Box>
      </Container>
    );
  }

  return (
    <Container data-testid="product-detail-skeleton" aria-busy>
      <Grid py={1} $twoColumn>
        {gallery}
        <Box p={1}>
          {header}
          <Box px={1}>{purchaseBlock}</Box>
          {description}
        </Box>
      </Grid>
    </Container>
  );
};

export default ProductDetailSkeleton;
